import { OverlapTransitionStateContext } from "@redotech/react-animation/outlet-transition";
import { OverlapTransitionState } from "@redotech/react-animation/transition";
import classnames from "classnames";
import { useObservableState } from "observable-hooks";
import {
  createContext,
  Dispatch,
  Fragment,
  memo,
  SetStateAction,
  useContext,
  useLayoutEffect,
  useMemo,
} from "react";
import { Link, useMatches } from "react-router-dom";
import { BehaviorSubject, combineLatest, Observable, of, Subject } from "rxjs";
import { map } from "rxjs/operators";
import * as breadcrumbCss from "./breadcrumb.module.css";
import { Flex } from "./flex";
import ChevronDown from "./icon-old/chevron-down.svg";
import { Text } from "./text";

export interface Breadcrumb {
  id: string;
  name: string;
  url: string | null;
}

export type BreadcrumbOverriders = Map<
  string,
  (breadcrumb: Breadcrumb) => Breadcrumb
>;
export const BreadcrumbOverridersContext = createContext<
  [BreadcrumbOverriders, Dispatch<SetStateAction<BreadcrumbOverriders>>]
>([new Map(), () => {}]);

const BREADCRUMB_DEFAULT = "";

export function breadcrumbSlot(): BreadcrumbSlot {
  return new BehaviorSubject(BREADCRUMB_DEFAULT);
}

export type BreadcrumbSlot = Subject<string>;

export function useBreadcrumb(
  breadcrumb: BreadcrumbSlot,
  value: string | undefined,
) {
  const transitionState = useContext(OverlapTransitionStateContext);
  const setValue = value || BREADCRUMB_DEFAULT;

  useLayoutEffect(() => {
    breadcrumb.next(setValue);

    // Skip cleanup during EXIT state to prevent overwriting the ENTER
    // component's breadcrumb value during OverlapTransition overlap window.
    if (transitionState === OverlapTransitionState.EXIT) {
      return undefined;
    }

    return () => breadcrumb.next(BREADCRUMB_DEFAULT);
  }, [breadcrumb, setValue, transitionState]);
}

export function useBreadcrumbs(): Breadcrumb[] | undefined {
  const matches = useMatches();
  const transitionState = useContext(OverlapTransitionStateContext);
  const [breadcrumbOverriders] = useContext(BreadcrumbOverridersContext);
  const breadcrumbs = useMemo(() => {
    const observables: Observable<Breadcrumb>[] = [];
    for (const match of matches) {
      const handle: any = match.handle;
      if (handle?.breadcrumb === undefined) {
        continue;
      }
      const name: Observable<string> =
        typeof handle.breadcrumb === "string"
          ? of(handle.breadcrumb)
          : handle.breadcrumb;
      const url =
        handle.breadcrumbNavigate !== false
          ? match.pathname.replace(/\/$/, "")
          : null;
      const id = match.id;

      observables.push(
        name.pipe(
          map((name) => {
            let breadcrumb = { id, name, url };
            for (const overrider of breadcrumbOverriders?.values() ?? []) {
              breadcrumb = overrider(breadcrumb);
            }
            return breadcrumb;
          }),
        ),
      );
    }

    return combineLatest(observables);
  }, [matches, breadcrumbOverriders]);

  const observable = useObservableState(breadcrumbs);

  if (transitionState === OverlapTransitionState.EXIT) {
    return undefined;
  }

  return observable;
}

export interface BreadcrumbsProps {
  size?: "small" | "large";
  /**
   * List of breadcrumb names to filter out if they appear as the first breadcrumb.
   * Useful for hiding top-level navigation categories (e.g., "Home", "Order Management")
   * from the breadcrumb trail.
   */
  skipFirstIfMatches?: string[];
}

export const Breadcrumbs = memo(function Breadcrumbs({
  size = "small",
  skipFirstIfMatches = [],
}: BreadcrumbsProps) {
  const breadcrumbs = useBreadcrumbs();
  if (!breadcrumbs) {
    return null;
  }

  // Skip the first breadcrumb if it matches one of the filter values
  let filteredBreadcrumbs =
    breadcrumbs.length > 1 &&
    skipFirstIfMatches.length > 0 &&
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    skipFirstIfMatches.includes(breadcrumbs[0].name)
      ? breadcrumbs.slice(1)
      : breadcrumbs;

  // Filter out breadcrumbs with empty names
  filteredBreadcrumbs = filteredBreadcrumbs.filter((b) => b.name !== "");

  return (
    <Flex
      align="center"
      className={classnames(breadcrumbCss.breadcrumbs, {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [breadcrumbCss.small]: size === "small",
      })}
      gap="xs"
    >
      {filteredBreadcrumbs.map((breadcrumb, i) => (
        <Fragment key={breadcrumb.id}>
          {0 < i && <ChevronDown className={breadcrumbCss.breadcrumbArrow} />}
          {breadcrumb.url ? (
            <Link
              onClick={(e) => (e.target as HTMLElement).blur()}
              to={breadcrumb.url}
            >
              <BreadcrumbText
                isLast={i < filteredBreadcrumbs.length - 1}
                isLink
                size={size}
                text={breadcrumb.name}
              />
            </Link>
          ) : (
            <BreadcrumbText
              isLast={i < filteredBreadcrumbs.length - 1}
              size={size}
              text={breadcrumb.name}
            />
          )}
        </Fragment>
      ))}
    </Flex>
  );
});

const BreadcrumbText = memo(function BreadcrumbText({
  text,
  isLast,
  isLink,
  size,
}: {
  text: string;
  isLast: boolean;
  isLink?: boolean;
  size: "small" | "large";
}) {
  return (
    <Text
      className={classnames(breadcrumbCss.breadcrumb, {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [breadcrumbCss.link]: isLink,
      })}
      fontSize={size === "small" ? "sm" : "lg"}
      {...(isLast
        ? { fontWeight: "medium", textColor: "placeholder" }
        : { fontWeight: "semibold", textColor: "primary" })}
    >
      {text}
    </Text>
  );
});
